// What a technician reads with no signal (PRD 95 §4.13): the page's own copy
// of the last good My day and today's visit screens — and the business list
// they open from — in Cache Storage, KEYED BY USER AND BUSINESS. Never a write
// queue: nothing is ever sent from here.
//
//   - READ ONLY ON A NETWORK FAILURE, never on a 4xx: the server's answer
//     always wins. A 401 or 403 purges that business's copies (the person may
//     have lost access); logout purges everything (`purgeAllFieldSnapshots`).
//   - Kept only while the business's `offline_snapshots` setting is on — an
//     owner whose technicians share phones turns it off, and the next answer
//     purges the copies.
//   - Never holds access notes, photos, signatures, PDFs or documents: those
//     come from their own endpoints and are never passed in here.
//   - Nothing older than yesterday, and 30 copies at most.

import { BusinessApiError } from '../../../services/businessApiCommon';
import { getGlobalBusinessId } from '../../../utils/apiUtils';

const CACHE_NAME = 'greenback-field-snapshots-v1';
const ORIGIN = 'https://field-snapshots.invalid';
const SAVED_AT = 'X-Saved-At';
const MAX_ENTRIES = 30;
const USER_KEY = 'user_uuid';

export type SnapshotKind = 'my-day' | 'workspace' | 'businesses' | 'checklists' | 'lines';

export interface Snapshot<T> {
  savedAt: string;
  data: T;
}

/** What a page got back: the server's answer, or (offline) a copy and when it was taken. */
export type MaybeOffline<T> = T & { offline_as_of?: string };

const available = (): boolean => typeof caches !== 'undefined';

function scope(kind: SnapshotKind): { user: string; business: string } | null {
  const user = localStorage.getItem(USER_KEY);
  const business = kind === 'businesses' ? '_' : getGlobalBusinessId();
  return user && business ? { user, business } : null;
}

function keyFor(kind: SnapshotKind, id: string): string | null {
  const owner = scope(kind);
  if (!owner) return null;
  return `${ORIGIN}/${encodeURIComponent(owner.user)}/${encodeURIComponent(owner.business)}/${kind}/${encodeURIComponent(id)}`;
}

/** A fetch that never reached the server, as opposed to a refusal from it. */
export function isNetworkFailure(error: unknown): boolean {
  if (error instanceof BusinessApiError) return false;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  return error instanceof TypeError;
}

/** The start of yesterday, local time: anything saved before it is dropped. */
function startOfYesterday(now: Date): number {
  const day = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  return day.getTime();
}

export async function saveFieldSnapshot<T>(kind: SnapshotKind, id: string, data: T, now = new Date()): Promise<void> {
  const key = keyFor(kind, id);
  if (!key || !available()) return;
  try {
    const cache = await caches.open(CACHE_NAME);
    await cache.put(key, new Response(JSON.stringify(data), {
      headers: { 'Content-Type': 'application/json', [SAVED_AT]: now.toISOString() },
    }));
    await prune(cache, now);
  } catch {
    // A full or blocked cache is no copy, never a broken page.
  }
}

export async function readFieldSnapshot<T>(kind: SnapshotKind, id: string, now = new Date()): Promise<Snapshot<T> | null> {
  const key = keyFor(kind, id);
  if (!key || !available()) return null;
  try {
    const cache = await caches.open(CACHE_NAME);
    const hit = await cache.match(key);
    if (!hit) return null;
    const savedAt = hit.headers.get(SAVED_AT) ?? '';
    if (!savedAt || Date.parse(savedAt) < startOfYesterday(now)) {
      await cache.delete(key);
      return null;
    }
    return { savedAt, data: (await hit.json()) as T };
  } catch {
    return null;
  }
}

/** Every copy this person holds for the current business. */
export async function purgeBusinessSnapshots(): Promise<void> {
  const owner = scope('my-day');
  if (!owner || !available()) return;
  try {
    const cache = await caches.open(CACHE_NAME);
    const prefix = `${ORIGIN}/${encodeURIComponent(owner.user)}/${encodeURIComponent(owner.business)}/`;
    const keys = await cache.keys();
    await Promise.all(keys.filter(request => request.url.startsWith(prefix)).map(request => cache.delete(request)));
  } catch {
    // Nothing to purge.
  }
}

/** Signing out leaves nothing behind on a shared phone. */
export async function purgeAllFieldSnapshots(): Promise<void> {
  if (!available()) return;
  try {
    await caches.delete(CACHE_NAME);
  } catch {
    // Nothing to purge.
  }
}

async function prune(cache: Cache, now: Date): Promise<void> {
  const keys = await cache.keys();
  const dated = await Promise.all(keys.map(async request => {
    const hit = await cache.match(request);
    return { request, at: Date.parse(hit?.headers.get(SAVED_AT) ?? '') || 0 };
  }));
  const cutoff = startOfYesterday(now);
  const stale = dated.filter(entry => entry.at < cutoff);
  const fresh = dated.filter(entry => entry.at >= cutoff).sort((a, b) => b.at - a.at);
  await Promise.all([ ...stale, ...fresh.slice(MAX_ENTRIES) ].map(entry => cache.delete(entry.request)));
}

export type KeepAnswer = 'keep' | 'skip' | 'purge';

/**
 * The server's answer — kept for offline reading when `keep(answer)` says
 * 'keep', left alone on 'skip' (someone else's day, an office read), and on
 * 'purge' (the business switched copies off) every copy for the business goes;
 * or, when the network fails, the last copy with `offline_as_of`. A refusal
 * from the server is never answered from a copy.
 */
export async function withFieldSnapshot<T extends object>(
  kind: SnapshotKind,
  id: string,
  fetcher: () => Promise<T>,
  keep: (answer: T) => KeepAnswer = () => 'keep'
): Promise<MaybeOffline<T>> {
  try {
    const answer = await fetcher();
    const decision = keep(answer);
    if (decision === 'keep') await saveFieldSnapshot(kind, id, answer);
    if (decision === 'purge') await purgeBusinessSnapshots();
    return answer;
  } catch (error) {
    if (error instanceof BusinessApiError && (error.status === 401 || error.status === 403)) {
      await purgeBusinessSnapshots();
    }
    if (isNetworkFailure(error)) {
      const copy = await readFieldSnapshot<T>(kind, id);
      if (copy) return { ...copy.data, offline_as_of: copy.savedAt };
    }
    throw error;
  }
}
